import { useEffect, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Loader2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import AuthService from '@/lib/auth';

interface AccessState {
  accessVerified?: boolean;
  patientName?: string;
  expiresAt?: string;
}

export default function QuestionnaireWithAccess() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [accessError, setAccessError] = useState('');
  const [accessState, setAccessState] = useState<AccessState>({});

  useEffect(() => {
    const verifyAccess = async () => {
      const searchParams = new URLSearchParams(location.search);
      const accessCode = searchParams.get('access_code');
      const state = location.state as AccessState | undefined;

      // If no access code or invalid state, deny access
      if (!accessCode || !state?.accessVerified) {
        setAccessError('Invalid or missing access code');
        setIsLoading(false);
        return;
      }

      // Check if access has expired
      if (state.expiresAt && new Date(state.expiresAt) < new Date()) {
        setAccessError('This access link has expired');
        setIsLoading(false);
        return;
      }

      // Verify the access code with the server (RDS via API)
      try {
        const result = await AuthService.verifyQuestionnaireAccess(accessCode);
        if (result?.error || !result) {
          throw new Error('Invalid access code');
        }

        // Additional verification logic can be added here
        setAccessState({
          accessVerified: true,
          patientName: state.patientName,
          expiresAt: state.expiresAt
        });
      } catch (err) {
        setAccessError('Failed to verify access. Please request a new link.');
      } finally {
        setIsLoading(false);
      }
    };

    verifyAccess();
  }, [location, id]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  if (accessError) {
    return (
      <div className="container max-w-2xl mx-auto p-4">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {accessError}
            <div className="mt-4">
              <Button onClick={() => navigate('/')}>
                Return to Home
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // Render the actual questionnaire component here
  return (
    <div className="container max-w-4xl mx-auto p-4">
      <h1 className="text-2xl font-bold mb-6">
        Health Questionnaire
        {accessState.patientName && ` for ${accessState.patientName}`}
      </h1>
      
      {/* Replace this with your actual questionnaire component */}
      <div className="bg-white p-6 rounded-lg shadow">
        <p className="text-gray-600 mb-4">
          This is where your questionnaire form would be displayed. for the patient {accessState.patientName}
        </p>
        <p className="text-sm text-gray-500">
          Access expires on: {accessState.expiresAt ? new Date(accessState.expiresAt).toLocaleString() : 'Unknown'}
        </p>
      </div>
    </div>
  );
}
